import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const AboutPreview = () => {
  const [skills, setSkills] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const fallbackSkills = [
        { name: "Remote Sensing & GIS", icon: "SatelliteDish" },
        { name: "Scientific Research & Modelling", icon: "Microscope" },
        { name: "Data Science & Forecasting", icon: "BarChart4" },
        { name: "Full Stack Development", icon: "Code2" }
      ];
      const fallbackAchievements = [
        { icon: "Award", title: "ISRO & NARL Researcher", description: "Completed advanced research internships at premier Indian space agencies" },
        { icon: "Users", title: "STEM Outreach to 500+ Students", description: "Inspired and mentored school students through national STEM initiatives" },
        { icon: "TrendingUp", title: "10+ Research & Tech Projects", description: "Delivered impactful academic, geospatial, and full-stack development projects" }
      ];

      try {
        const [skillsRes, achievementsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/skills`),
          fetch(`${API_BASE_URL}/api/achievements`)
        ]);
        
        const skillsData = await skillsRes.json();
        const achievementsData = await achievementsRes.json();
        
        let allSkillsFlattened = [];
        if (Array.isArray(skillsData)) {
          skillsData.forEach(skillCategory => {
            try {
              const skillArray = JSON.parse(skillCategory.name);
              if (Array.isArray(skillArray)) {
                skillArray.forEach(item => {
                  const itemName = typeof item === 'string' ? item : item.name;
                  const itemIcon = typeof item === 'object' && item.icon ? item.icon : skillCategory.icon;
                  allSkillsFlattened.push({ name: itemName, icon: itemIcon });
                });
              } else {
                allSkillsFlattened.push({ name: skillCategory.name, icon: skillCategory.icon });
              }
            } catch (e) {
              allSkillsFlattened.push({ name: skillCategory.name, icon: skillCategory.icon });
            }
          });
        }

        const safeAchievements = Array.isArray(achievementsData) ? achievementsData : [];

        setSkills(allSkillsFlattened.length > 0 ? allSkillsFlattened : fallbackSkills);
        setAchievements(safeAchievements.length > 0 ? safeAchievements : fallbackAchievements);
      } catch (err) {
        console.error('Error fetching about preview data:', err);
        setSkills(fallbackSkills);
        setAchievements(fallbackAchievements);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);


  return (
    <section className="py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary">
                Turning Complex Problems Into 
                <span className="text-accent"> Innovative, Impact-Driven Solutions</span>
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                With a strong foundation in Computer Science and hands-on experience at institutions like <b>ISRO–NESAC, NASA, and ISRO-NARL</b>. I specialize in transforming complex scientific and technical problems into intelligent, user-friendly solutions. My work combines research precision, engineering depth, and design thinking to deliver results that are not only technically sound but also impactful and scalable.
              </p>
              <p className="text-text-secondary leading-relaxed">
                I believe that true innovation happens at the intersection of technology, science, and empathy—where code meets purpose, and design meets data. Whether it's building a weather system, analyzing Mars clouds, or architecting UAV-based health analytics, I bring clarity, creativity, and commitment to every challenge.
              </p>
            </div>

            {/* Achievements */}
            <div className="space-y-4">
              {loading ? (
                <div className="h-48 animate-pulse bg-white/5 rounded-xl"></div>
              ) : (
                achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5 transition-smooth border border-transparent hover:border-white/5">
                    <div className="flex-shrink-0 w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                      <Icon name={achievement.icon} size={20} className="text-accent" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-text-primary mb-1">
                        {achievement.title}
                      </h4>
                      <p className="text-sm text-text-secondary">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* CTA */}
            <div className="pt-4">
              <Button variant="primary" size="lg" iconName="User" iconPosition="left">
                <Link 
                  to="/about"
                  title="Read Sudhan R's Professional Story and Background"
                  aria-label="Learn More About Sudhan R"
                >
                  Learn More About Me
                </Link>
              </Button>
            </div>
          </div>

          {/* Skills */}
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl">
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-6">
                Core Expertise
              </h3>
              <div className="space-y-6">
                {loading ? (
                  <div className="h-64 animate-pulse bg-white/10 rounded-xl"></div>
                ) : (
                  <>
                    {skills.slice(0, 5).map((skill, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-accent/30 transition-smooth group">
                        <div className="flex-shrink-0 w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center group-hover:bg-accent/20 transition-smooth">
                          <Icon name={skill.icon || 'Star'} size={20} className="text-accent" />
                        </div>
                        <span className="font-heading font-semibold text-text-primary tracking-wide">
                          {skill.name}
                        </span>
                      </div>
                    ))}
                    {skills.length > 5 && (
                      <div className="pt-2">
                        <Link 
                          to="/about#skills" 
                          title="View Comprehensive Technical Skills Breakdown"
                          aria-label="View All Technical Skills and Competencies"
                          className="flex items-center justify-center w-full py-3 bg-white/5 border border-white/10 rounded-xl font-heading font-semibold text-accent hover:bg-accent/10 hover:border-accent/30 transition-smooth"
                        >
                          Explore All {skills.length} Skills
                          <Icon name="ArrowRight" size={16} className="ml-2" />
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 shadow-lg text-center">
                <div className="font-heading font-bold text-2xl text-text-primary mb-1">5+</div>
                <div className="text-sm text-text-secondary"> Research Projects Completed</div>
              </div>
              <div className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 shadow-lg text-center">
                <div className="font-heading font-bold text-2xl text-text-primary mb-1">8+</div>
                <div className="text-sm text-text-secondary">Domains Explored</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;